// services/feedbackService.ts — Chat response feedback (thumbs up / thumbs down)
//
// Wraps the /api/feedback endpoints with the same axios + bearer-token pattern
// as agentService / dashboardBuilderService. Used by the FeedbackBar component
// rendered below each assistant message in UnifiedChat.

import axios, { AxiosInstance } from 'axios';
import { authService } from './authService';
import { apiConfig } from '../config/apiConfig';

export type FeedbackRating = 'up' | 'down';

export interface FeedbackSubmitPayload {
  agentId: string;
  sessionId: string;
  messageId: string;
  rating: FeedbackRating;
  comment?: string | null;
  query?: string | null;
  response?: string | null;
  sqlText?: string | null;
  rowCount?: number | null;
  trace?: unknown;
  model?: string | null;
  responseTimeMs?: number | null;
}

export interface FeedbackSubmitResponse {
  id: string;
  rating: FeedbackRating;
}

export interface FeedbackItem {
  id: string;
  messageId: string;
  rating: FeedbackRating;
  comment: string | null;
  updatedAt: string;
}

class FeedbackService {
  private ax: AxiosInstance;

  constructor() {
    this.ax = axios.create({
      baseURL: apiConfig.baseUrl,
      timeout: 15000,
      headers: { 'Content-Type': 'application/json' },
    });

    this.ax.interceptors.request.use(async (config) => {
      const base = (config.baseURL || '').toString();
      if (base.includes('localhost:3001')) return config;
      try {
        const token = await authService.getAccessToken();
        if (token) config.headers.Authorization = `Bearer ${token}`;
      } catch {
        // allow request to fail naturally — UI handles errors quietly
      }
      return config;
    });
  }

  async submit(payload: FeedbackSubmitPayload): Promise<FeedbackSubmitResponse> {
    const res = await this.ax.post<FeedbackSubmitResponse>('/api/feedback', payload);
    return res.data;
  }

  async deleteById(id: string): Promise<void> {
    await this.ax.delete(`/api/feedback/${encodeURIComponent(id)}`);
  }

  async deleteByMessage(sessionId: string, messageId: string): Promise<void> {
    await this.ax.delete('/api/feedback', { params: { sessionId, messageId } });
  }

  async listForSession(sessionId: string): Promise<FeedbackItem[]> {
    const res = await this.ax.get<{ items: FeedbackItem[] }>('/api/feedback/mine', {
      params: { sessionId },
    });
    return res.data?.items || [];
  }
}

export const feedbackService = new FeedbackService();
